import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, Filter, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import { homepageContentService } from '../../services/api/homepageContentService';
import type { HomepageContent } from '../../types/homepage-content.types';

const HomepageContentList: React.FC = () => {
  const navigate = useNavigate();
  const [contents, setContents] = useState<HomepageContent[]>([]);
  const [filteredContents, setFilteredContents] = useState<HomepageContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<HomepageContent | null>(null);

  const contentTypes = [
    { value: '', label: 'All Types' },
    { value: 'carousel_banner', label: 'Carousel Banner' },
    { value: 'sidebar_image', label: 'Sidebar Image' },
    { value: 'welcome_section', label: 'Welcome Section' },
    { value: 'vision_section', label: 'Vision Section' },
    { value: 'objective_section', label: 'Objective Section' },
    { value: 'portal_logo', label: 'Portal Logo' },
    { value: 'header_logo', label: 'Header Logo' },
    { value: 'header_leader', label: 'Header Leader' },
  ];

  useEffect(() => {
    fetchContents();
  }, []);

  useEffect(() => {
    filterContents();
  }, [searchTerm, selectedType, contents]);

  const fetchContents = async () => {
    try {
      setLoading(true);
      const response = await homepageContentService.getAll();
      setContents(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch homepage content');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterContents = () => {
    let filtered = [...contents];

    // Filter by type
    if (selectedType) {
      filtered = filtered.filter((content) => content.content_type === selectedType);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (content) =>
          content.title.toLowerCase().includes(term) ||
          content.description?.toLowerCase().includes(term) ||
          content.content_type.toLowerCase().includes(term)
      );
    }

    setFilteredContents(filtered);
  };

  const handleDelete = async () => {
    if (!contentToDelete) return;

    try {
      await homepageContentService.delete(contentToDelete.id);
      toast.success('Content deleted successfully');
      setShowDeleteModal(false);
      setContentToDelete(null);
      fetchContents();
    } catch (error) {
      toast.error('Failed to delete content');
      console.error(error);
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await homepageContentService.toggleStatus(id);
      toast.success('Status updated successfully');
      fetchContents();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const getTypeBadgeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      carousel_banner: 'bg-purple-100 text-purple-800',
      sidebar_image: 'bg-blue-100 text-blue-800',
      welcome_section: 'bg-green-100 text-green-800',
      vision_section: 'bg-yellow-100 text-yellow-800',
      objective_section: 'bg-pink-100 text-pink-800',
      portal_logo: 'bg-indigo-100 text-indigo-800',
      header_logo: 'bg-teal-100 text-teal-800',
      header_leader: 'bg-orange-100 text-orange-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      carousel_banner: 'Carousel Banner',
      sidebar_image: 'Sidebar Image',
      welcome_section: 'Welcome Section',
      vision_section: 'Vision Section',
      objective_section: 'Objective Section',
      portal_logo: 'Portal Logo',
      header_logo: 'Header Logo',
      header_leader: 'Header Leader',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading homepage content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Homepage Content</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage homepage content, banners, and sections
          </p>
        </div>
        <button
          onClick={() => navigate('/homepage-content/create')}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <Plus size={20} className="mr-2" />
          Add Content
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              {contentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredContents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    No content found
                  </td>
                </tr>
              ) : (
                filteredContents.map((content) => (
                  <tr key={content.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold ${getTypeBadgeColor(
                          content.content_type
                        )}`}
                      >
                        {getTypeLabel(content.content_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{content.title}</div>
                      {content.sub_title && (
                        <div className="text-xs text-gray-500 mt-1">{content.sub_title}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {content.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {content.image_url ? (
                        <img
                          src={content.image_url}
                          alt={content.title}
                          className="w-16 h-10 object-cover rounded border border-gray-200"
                        />
                      ) : (
                        <span className="text-xs text-gray-400">No image</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {content.display_order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(content.id)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          content.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {content.is_active ? (
                          <>
                            <Eye size={12} className="mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <EyeOff size={12} className="mr-1" />
                            Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => navigate(`/homepage-content/edit/${content.id}`)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setContentToDelete(content);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {contentTypes.slice(1).map((type) => {
          const count = contents.filter((c) => c.content_type === type.value).length;
          return (
            <div key={type.value} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="text-xs font-medium text-gray-600">{type.label}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{contentToDelete?.title}"? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setContentToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomepageContentList;